import React, { useEffect, ReactElement, ForwardedRef } from 'react';
import TextField from '@mui/material/TextField';
import GoogleMapService from '../../services/google-map-service';
import FormatService from '../../services/format-service';
import ScreenService from '../../services/screen-service';
import useShiftState from '../../hooks/use-shift-state';
import useStylesFieldAddress from './field-address.styles';
import { KEYBOARD_KEY_ENTER, KEYBOARD_KEY_TAB, KEYBOARD_KEY_SHIFT } from '../../constants/system/keyboard-keys';
import { DATA_REF_NAME } from '../../constants/system/data-attributes';
import './field-address.styles.scss';

const FieldAddress: ReactMemoComponent = React.memo(React.forwardRef((
  props: ReactComponentProps,
  ref: ForwardedRef<HTMLDivElement>,
): ReactElement => {
  const PLACEHOLDER_DEFAULT: string = 'Enter a location';

  const {
    isModal,
    isBordered,
    isOutlined,
    withUnderline,
    itemId,
    label,
    placeholder,
    className,
    inputRefs,
    inputRefIndex,
    onKeyDown,
    onAddressSearchCompleted,
    input,
    meta,
    ...custom
  } = props;

  const {
    touched: isTouched,
    invalid: isInvalid,
    error: formError,
    submitError: formSubmitError,
    modifiedSinceLastSubmit,
  } = (meta || {}) as FormFieldMetaProps;

  const { classes } = useStylesFieldAddress();
  const isShiftPressed: boolean = useShiftState();

  const borderedClassName: string | undefined = isBordered ? classes.fieldAddressBordered : undefined;
  const outlinedClassName: string | undefined = isOutlined ? classes.fieldAddressOutlined : undefined;
  const noUnderlineClassName: string | undefined = !withUnderline ? classes.fieldAddressNoUnderline : undefined;

  const withErrors: boolean = ((isTouched && isInvalid) || !!formError || !!formSubmitError)
    && !modifiedSinceLastSubmit;
  const fieldIdPrefix: string = isModal ? 'address-field-modal' : 'address-field';
  const fieldId: string = `${fieldIdPrefix}${itemId ? `-${itemId}` : ''}`;
  const fieldElement: HTMLElement | null = ScreenService.getElementById(fieldId);

  const onAddressFieldKeyDown: Function = (event: React.KeyboardEvent): void => {
    if (event.key === KEYBOARD_KEY_SHIFT || event.key === KEYBOARD_KEY_TAB || isShiftPressed) {
      event.stopPropagation();

      return;
    }

    if (event.key === KEYBOARD_KEY_ENTER) {
      event.preventDefault();
    }

    if (onKeyDown) {
      onKeyDown();
    }
  };

  useEffect((): void => {
    if (isShiftPressed) {
      ScreenService.removeAllElementsByClassName(GoogleMapService.GOOGLE_MAPS_CLASSNAME_ELEMENT_DROPDOWN);
    }

    if (!isShiftPressed && fieldElement) {
      GoogleMapService.addressAutocomplete(fieldElement as HTMLInputElement, (
        searchResult: GoogleMapsSearchResult,
      ): void => {
        onAddressSearchCompleted(searchResult);
      });
    }

    if (isShiftPressed && inputRefs) {
      GoogleMapService.removeAllListeners(inputRefs.current[inputRefIndex]);
    }
  }, [isShiftPressed, fieldElement]);

  return (
    <TextField
      ref={ref}
      id={fieldId}
      label={label}
      placeholder={placeholder || label || PLACEHOLDER_DEFAULT}
      error={withErrors}
      helperText={withErrors && (formError || formSubmitError)}
      className={FormatService.formatClassNames(
        classes.fieldAddress, borderedClassName, outlinedClassName, noUnderlineClassName, className,
      )}
      onKeyDown={onAddressFieldKeyDown}
      inputProps={{
        [DATA_REF_NAME]: inputRefIndex,
      }}
      inputRef={(
        inputRefs ? (ref: HTMLInputElement | null): void => {
          if (inputRefs.current) {
            inputRefs.current[inputRefIndex] = ref;
          }
        } : null
      )}
      {...input}
      {...custom}
    />
  );
}));

export default FieldAddress;
